import { useState, useCallback } from 'react';
import { getDatabase, ref, get, set, serverTimestamp } from 'firebase/database';
import { app } from '../../../../firebase'; // Import main Firebase app
import { config } from '../../../../config';

// Initialize RTDB using the main Firebase app (includes authentication context)
const RTDB_URL = 'https://just-rhythm-445002-s8-e92d0-513eb.firebaseio.com/';

let rtdb;

try {
  // Use main Firebase app with authentication context
  rtdb = getDatabase(app, RTDB_URL);
  console.log('%c✅ RTDB INITIALIZED', 'background: green; color: white; font-size: 20px; padding: 10px;');
  console.log('RTDB URL:', RTDB_URL);
  console.log('Using main app instance:', !!app);
} catch (error) {
  console.error('%c❌ RTDB INIT FAILED', 'background: red; color: white; font-size: 20px; padding: 10px;', error);
}

const useQuickPredictionsALL = () => {
  const [loading, setLoading] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [stopRequested, setStopRequested] = useState(false);

  // Format date to ET timezone YYYY-MM-DD
  const formatDateKey = (date) => {
    const nycDate = new Date(date.toLocaleString("en-US", { timeZone: "America/New_York" }));
    const year = nycDate.getFullYear();
    const month = String(nycDate.getMonth() + 1).padStart(2, '0');
    const day = String(nycDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get games from settlement service for a specific date
  const getTodaysGames = async (dateString = null) => {
    try {
      const dateKey = dateString || formatDateKey(new Date());

      console.log('Fetching games for date:', dateKey);

      // Get games from manual settlement service (stored at manual-settlements/football/{date})
      const settlementRef = ref(rtdb, `manual-settlements/football/${dateKey}`);
      const snapshot = await get(settlementRef);

      if (!snapshot.exists()) {
        console.log('No games found for today:', dateKey);
        return [];
      }

      const games = [];
      const settlementData = snapshot.val();

      // Convert the object of games to array
      Object.entries(settlementData).forEach(([gameId, game]) => {
        // Only add if it's a game object (has teams property)
        if (game && game.teams) {
          games.push({
            ...game,
            gameId: gameId
          });
        }
      });

      console.log(`Found ${games.length} total games for ${dateKey}`);

      // Sort games by timestamp (check if timestamp exists)
      games.sort((a, b) => {
        const timestampA = a.timestamp || 0;
        const timestampB = b.timestamp || 0;
        return timestampA - timestampB;
      });

      return games;
    } catch (error) {
      console.error('Error fetching today\'s games:', error);
      throw error;
    }
  };

  // Build prompt for multiple games (matching PredictionService format)
  const buildMultipleGamesPrompt = (games) => {
    const gamesList = games.map((game, index) => {
      const homeTeam = game.teams.home.name;
      const awayTeam = game.teams.away.name;
      const homeTeamId = game.teams.home.id;
      const awayTeamId = game.teams.away.id;
      const leagueId = game.league.id;

      // Only include IDs if they exist
      let idInfo = '';
      if (homeTeamId && awayTeamId && leagueId) {
        idInfo = ` (Home ID: ${homeTeamId}, Away ID: ${awayTeamId}, League ID: ${leagueId})`;
      }

      return `${index + 1}. ${homeTeam} vs ${awayTeam}${idInfo}`;
    }).join('\n');

    return `QUICK PICK DASHBOARD - BE CONCISE! No verbose explanations.

Predict these ${games.length} football/soccer games:

${gamesList}

For EACH game, respond with ONLY:
[Game #]: [Winner] | [CONFIDENCE %] | [HIGH/MED/LOW] | [5-10 word reason]

IMPORTANT: Confidence must be a number between 0.1 and 99.9 (no more than one decimal place)

Example format:
1: Liverpool | 87.5 | HIGH | Better form and home advantage
2: Barcelona | 62.3 | MED | Key injuries but superior squad

BE BRIEF - MAX 15 WORDS PER GAME!`;
  };

  // Run predictions for a batch of games
  const runBatchPredictions = async (games) => {
    try {
      const prompt = buildMultipleGamesPrompt(games);

      // Call the backend API (same as PredictionService does)
      const response = await fetch(`${config.apiUrl}/ai/ask`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          question: prompt,
          history: [],
          language: 'en',
          isGuest: false,
          metadata: {
            userId: 'pickstar-system',
            sessionId: `pickstar-${Date.now()}`,
            conversationId: `pickstar-batch-${Date.now()}`
          }
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      // Handle streaming response (same as PredictionService)
      let predictionText = "";
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        const lines = buffer.split('\n');
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'content' && data.text) {
                predictionText += data.text;
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
      // Parse multiple game predictions using the same parser as PredictionService
      const predictions = [];
      const lines = predictionText.split('\n').filter(line => line.trim());

      // Look for patterns like "1: Team | XX.X | CONFIDENCE | Reason"
      const predictionPattern = /^(\d+)[:.]?\s*(.+?)\s*\|\s*([\d.]+)\s*\|\s*(HIGH|MED|LOW)\s*\|\s*(.+)$/i;

      games.forEach((game, index) => {
        let found = false;
        const gameNum = index + 1;

        // Look for this game's prediction
        for (const line of lines) {
          const match = line.match(predictionPattern);
          if (match && parseInt(match[1]) === gameNum) {
            const [, , winner, confidence, level, reason] = match.map(s => s.trim());

            predictions.push({
              gameId: game.gameId,
              fixture: game.fixture || { timestamp: game.timestamp },
              teams: game.teams,
              league: game.league,
              status: game.status,
              venue: game.venue,
              timestamp: game.timestamp,
              prediction: {
                winner: winner,
                confidence: parseFloat(confidence),
                level: level.toUpperCase(),
                reason: reason,
                rawResponse: line,
                timestamp: Date.now()
              }
            });
            found = true;
            break;
          }
        }

        // If no prediction found for this game, add a fallback
        if (!found) {
          predictions.push({
            gameId: game.gameId,
            fixture: game.fixture || { timestamp: game.timestamp },
            teams: game.teams,
            league: game.league,
            status: game.status,
            venue: game.venue,
            timestamp: game.timestamp,
            prediction: {
              winner: 'Draw',
              confidence: 50.0,
              level: 'LOW',
              reason: 'Failed to parse prediction',
              error: 'No prediction found for this game',
              rawResponse: predictionText,
              timestamp: Date.now()
            }
          });
        }
      });

      return predictions;
    } catch (error) {
      console.error('Error running batch predictions:', error);
      // Return error for all games
      return games.map(game => ({
        gameId: game.gameId,
        fixture: game.fixture || { timestamp: game.timestamp },
        teams: game.teams,
        league: game.league,
        status: game.status,
        venue: game.venue,
        timestamp: game.timestamp,
        prediction: {
          error: error.message,
          timestamp: Date.now()
        }
      }));
    }
  };

  // Run predictions for selected games
  const runPredictionsForSelectedGames = useCallback(async (selectedGamesArray = null, dateString = null) => {
    setLoading(true);
    setError(null);
    setPredictions([]);
    setProgress({ current: 0, total: 0 });
    setStopRequested(false);

    try {
      let gamesToPredict;

      if (selectedGamesArray && selectedGamesArray.length > 0) {
        // Use the provided selected games
        gamesToPredict = selectedGamesArray;
        console.log(`Processing ${gamesToPredict.length} selected games`);
      } else {
        // Fallback to all games (original behavior)
        const games = await getTodaysGames();

        if (games.length === 0) {
          setError('No games found. Make sure settlement service has run.');
          setLoading(false);
          return;
        }

        // Filter for games we want to predict (not finished games)
        gamesToPredict = games.filter(game => {
          const status = game.status?.short;
          return !status || ['NS', '1H', 'HT', '2H', 'SUSP', 'INT'].includes(status);
        });
      }

      console.log(`Processing ${gamesToPredict.length} games for predictions`);

      if (gamesToPredict.length === 0) {
        setError('No eligible games for predictions (all games may be finished)');
        setLoading(false);
        return;
      }

      setProgress({ current: 0, total: gamesToPredict.length });

      // Run predictions in batches to avoid rate limiting
      const batchSize = 5;
      const results = [];

      for (let i = 0; i < gamesToPredict.length; i += batchSize) {
        // Check if stop was requested
        if (stopRequested) {
          setError('Prediction run stopped by user');
          console.log('Stopping predictions at user request');
          break;
        }

        const batch = gamesToPredict.slice(i, i + batchSize);
        const batchResults = await runBatchPredictions(batch);
        results.push(...batchResults);

        setProgress({ current: Math.min(i + batchSize, gamesToPredict.length), total: gamesToPredict.length });
        setPredictions(prevPredictions => [...prevPredictions, ...batchResults]);

        // Add a small delay between batches to avoid rate limiting
        if (i + batchSize < gamesToPredict.length) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }

      // Store all predictions in RTDB - use provided date or today
      const dateKey = dateString || formatDateKey(new Date());
      const pickstarPredictionsRef = ref(rtdb, `pickstar-predictions/football/${dateKey}`);

      console.log(`Storing ${results.length} predictions to RTDB at path: pickstar-predictions/football/${dateKey}`);

      if (results.length === 0) {
        console.warn('No predictions to store - results array is empty');
        return;
      }

      // Clean undefined values from predictions (Firebase doesn't allow undefined)
      const cleanObject = (obj) => {
        if (obj === null || obj === undefined) return null;
        if (typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(cleanObject);

        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
          if (value !== undefined) {
            cleaned[key] = cleanObject(value);
          }
        }
        return cleaned;
      };

      const predictionsData = {
        predictions: results.reduce((acc, pred) => {
          // Clean the prediction object to remove undefined values
          acc[pred.gameId] = cleanObject(pred);
          return acc;
        }, {}),
        metadata: {
          runTimestamp: serverTimestamp(),
          totalGames: results.length,
          successfulPredictions: results.filter(r => !r.prediction.error).length,
          failedPredictions: results.filter(r => r.prediction.error).length,
          savedAt: new Date().toISOString()
        }
      };

      console.log('About to write to RTDB:', {
        path: `pickstar-predictions/football/${dateKey}`,
        databaseURL: RTDB_URL,
        dataSize: JSON.stringify(predictionsData).length,
        predictionsCount: Object.keys(predictionsData.predictions).length,
        rtdbInitialized: !!rtdb,
        authState: 'checking...'
      });

      // Check authentication state before writing
      const { auth } = await import('../../../../firebase');
      const currentUser = auth.currentUser;
      console.log('Current auth state:', {
        authenticated: !!currentUser,
        userId: currentUser?.uid,
        email: currentUser?.email
      });

      try {
        await set(pickstarPredictionsRef, predictionsData);
        console.log('%c✅ RTDB WRITE SUCCESSFUL!', 'background: green; color: white; font-size: 16px; padding: 8px;');
        console.log(`📍 Stored ${results.length} predictions at: pickstar-predictions/football/${dateKey}`);
        console.log('🔗 Firebase Console URL:', `https://console.firebase.google.com/project/just-rhythm-445002-s8-e92d0/database/just-rhythm-445002-s8-e92d0-513eb/data/~2Fpickstar-predictions~2Ffootball~2F${dateKey}`);
      } catch (writeError) {
        console.error('❌ RTDB Write Error Details:', {
          error: writeError,
          message: writeError.message,
          code: writeError.code,
          stack: writeError.stack,
          path: `pickstar-predictions/football/${dateKey}`,
          authenticated: !!currentUser,
          rtdbUrl: RTDB_URL
        });
        throw writeError;
      }

      // Verify the write by reading it back
      const verifyRef = ref(rtdb, `pickstar-predictions/football/${dateKey}`);
      const verifySnapshot = await get(verifyRef);
      if (verifySnapshot.exists()) {
        console.log('✅ Verified: Data successfully written to RTDB');
        const verifyData = verifySnapshot.val();
        console.log('Stored predictions count:', Object.keys(verifyData.predictions || {}).length);
      } else {
        console.error('❌ ERROR: Data was not found in RTDB after write!');
      }
      setProgress({ current: gamesToPredict.length, total: gamesToPredict.length });

    } catch (error) {
      console.error('Error running predictions:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [stopRequested]);

  // Keep backward compatibility with old function name
  const runPredictionsForAllGames = useCallback(async () => {
    return runPredictionsForSelectedGames();
  }, [runPredictionsForSelectedGames]);

  // Get existing predictions for a specific date
  const getTodaysPredictions = useCallback(async (dateString = null) => {
    console.log('%c📥 LOADING PREDICTIONS FROM RTDB', 'background: purple; color: white; font-size: 14px; padding: 6px;');
    try {
      const dateKey = dateString || formatDateKey(new Date());
      console.log('Date key:', dateKey);
      console.log('RTDB initialized?', !!rtdb);

      const pickstarPredictionsRef = ref(rtdb, `pickstar-predictions/football/${dateKey}`);
      console.log('Fetching from path:', `pickstar-predictions/football/${dateKey}`);

      const snapshot = await get(pickstarPredictionsRef);
      console.log('Snapshot exists?', snapshot.exists());

      if (snapshot.exists()) {
        const data = snapshot.val();
        console.log('%c✅ PREDICTIONS FOUND!', 'background: green; color: white; font-size: 14px; padding: 6px;');
        console.log('Raw data:', data);
        const predictionsArray = Object.values(data.predictions || {});
        console.log(`Loaded ${predictionsArray.length} predictions from RTDB`);
        setPredictions(predictionsArray);
        return predictionsArray;
      }

      console.log('%c⚠️ NO PREDICTIONS FOUND', 'background: orange; color: white; font-size: 14px; padding: 6px;');
      console.log('Searched path:', `pickstar-predictions/football/${dateKey}`);
      setPredictions([]);
      return [];
    } catch (error) {
      console.error('Error fetching predictions:', error);
      setError(error.message);
      return [];
    }
  }, []);

  // Stop the current prediction run
  const stopPredictions = useCallback(() => {
    setStopRequested(true);
    console.log('Stop requested by user');
  }, []);

  return {
    loading,
    predictions,
    error,
    progress,
    runPredictionsForAllGames,
    runPredictionsForSelectedGames,
    getTodaysPredictions,
    getTodaysGames,
    stopPredictions
  };
};

export default useQuickPredictionsALL;